import type { Transport } from "@tdelivr/api-client";

import type { JourneyView } from "./ride-journey.ts";

/**
 * Every request this application makes, in one place, with the reason each one goes where it does.
 *
 * ## The journey reads go to EXP-002. Everything before a ride exists does not, and that is a
 * decision rather than an exception
 *
 * Document 17 WEB-0743 forbids making every microservice directly callable from a channel, and
 * WEB-0065 asks for cross-service views to be composed server-side. EXP-002's `ride-journeys`
 * composes **eight** services behind one screen — MOB-03, MOB-02, TRP-01, DSP-03, TRP-02, DRV-01,
 * RAT-03 and GEO-04 — and seven of the nine screens this build draws read it and nothing else. That
 * is the fan-out WEB-0743 is about, and this application makes none of it for itself.
 *
 * The two screens before a ride request exists are a different shape, and
 * [ADR 0026](../../../docs/adr/0026-a-layer-with-no-subject-is-read-through-one-that-has.md)
 * section 1 settled it explicitly rather than leaving it to be guessed: the class chooser was
 * **considered for EXP-002 and rejected**, because "the only way to get a price is
 * `POST /v1/ride-quotes`, which mints an immutable priced resource. A BFF issuing three of them to
 * draw one screen would be deciding how many quotes a tenant holds, which is a write and a domain
 * decision at once." EXP-003's own rule is the other half of it: a screen that reads one listing
 * reads it directly. So:
 *
 *   - `2 Choose` reads MOB-01's product listing directly. One listing, one service.
 *   - `2 Choose` and `3 Quote` write MOB-02 and MOB-03 directly, because they are commands and ADR
 *     0024's position on the driver side applies unchanged: a BFF that relayed a command would be
 *     the proxy WEB-0743 forbids wearing a BFF's name.
 *   - `1 Where to` and `2 Choose` call GEO-01, GEO-02 and GEO-04 directly. All three are
 *     computations rather than records — a coordinate in, a place or a distance out — and there is
 *     no composition to do.
 *
 * ## Which commands carry an `Idempotency-Key`, and which deliberately do not
 *
 * `CLAUDE.md` section 2: send one wherever the spec marks one, and a "check status" action is a read
 * rather than a retry of the command. Four operations here are marked idempotent and all four are
 * given a key; the rest are not and are not. `POST /v1/ride-quotes/{id}/validate` is the interesting
 * one: it is a `POST`, it is **not** marked idempotent, and it is a read — it answers whether a
 * price is still held. It mints nothing and it is called on every render of `3 Quote`, which is
 * exactly what `CLAUDE.md`'s sentence about checking status describes.
 */

/* ------------------------------------------------------------------------ what the services say */

/** GEO-02's answer. The only way a coordinate becomes a place name on this platform. */
export interface ReverseGeocodeAnswer {
  readonly address: {
    readonly formatted: string;
    readonly countryCode?: string;
    readonly locality?: string;
    readonly administrativeArea?: string;
    readonly provider?: string;
    readonly confidence?: "rooftop" | "street" | "locality" | "unknown";
  };
  readonly cacheHit?: boolean;
}

/** GEO-01's answer, which is `1 Where to`'s destination search. */
export interface GeocodeAnswer {
  readonly results: readonly {
    readonly id: string;
    readonly formattedAddress: string;
    readonly position: { readonly latitude: number; readonly longitude: number };
    readonly confidence?: number;
    readonly precision?: "rooftop" | "parcel" | "street" | "locality" | "region" | "unknown";
  }[];
}

/** GEO-04's answer, which is `2 Choose`'s `24 km · about 38 min`. */
export interface DistanceEtaAnswer {
  readonly result: {
    readonly distanceMetres: number;
    readonly durationSeconds: number;
    readonly routeDurationSeconds?: number;
    readonly trafficAdjustmentSeconds?: number;
    readonly calculatedAt: string;
    readonly freshUntil: string;
  };
}

/** The travel modes GEO-04 accepts, which are exactly the modes MOB-01's product publishes. */
export type TravelMode = "car" | "motorcycle" | "bicycle" | "walking";

/** One of MOB-01's ride products, narrowed to what `2 Choose` draws. */
export interface RideProduct {
  readonly productId: string;
  readonly displayName: string;
  readonly description?: string;
  readonly vehicleClass: string;
  readonly travelMode: TravelMode;
  readonly passengerCapacity: number;
  readonly luggageCapacity: number;
  readonly supportedRiderCapabilities?: readonly string[];
  readonly lifecycle: "draft" | "active" | "retired";
}

/** MOB-02's quote, as both its create and its validate answer it. */
export interface RideQuote {
  readonly id: string;
  readonly tenantId: string;
  readonly product: { readonly id: string; readonly mode: TravelMode };
  readonly route: { readonly distanceMetres: number; readonly durationSeconds: number };
  readonly fare: {
    readonly currency: string;
    readonly totalMinor: number;
    readonly lineItems?: readonly { readonly code: string; readonly amountMinor: number }[];
  };
  readonly origin: { readonly latitude: number; readonly longitude: number };
  readonly destination: { readonly latitude: number; readonly longitude: number };
  readonly createdAt: string;
  readonly expiresAt: string;
}

/**
 * MOB-02's verdict on a quote, which is the whole of `3b Quote expired`.
 *
 * `reason` is `valid | expired` and it is the field the measurement names as the source of
 * _"That price has expired"_ — the expiry verdict itself, rather than a comparison this application
 * makes against its own clock. Both are published, which matters: a countdown drawn on a phone runs
 * on that phone's clock, and which of the two screens a rider sees must not depend on whose clock is
 * right. EXP-002 publishes the same pair for the same reason, as `expired` beside `observedAt`.
 */
export interface QuoteValidation {
  readonly quote: RideQuote;
  readonly valid: boolean;
  readonly reason: "valid" | "expired";
}

/** MOB-03's ride request, as its create answers one. */
export interface RideRequestAnswer {
  readonly request: {
    readonly id: string;
    readonly tenantId: string;
    readonly passengerId: string;
    readonly status: "requested" | "cancelled";
    readonly version: number;
    readonly createdAt: string;
  };
}

/**
 * MOB-03's ride request as its own `GET` answers it, which is four facts the create answer's
 * narrowing drops.
 *
 * `RideRequestAnswer` above is the shape `3 Quote` needs from the booking command: the identifier to
 * carry forward, and nothing else. This is the same record read back by
 * `GET /v1/ride-requests/{id}`, which [ADR 0028](../../../docs/adr/0028-a-ride-request-is-read-by-two-audiences-and-the-rider-is-bounded-by-the-record.md)
 * gave a rider-side audience under `ride-request:self`, and the search screens need four things from
 * it that nothing else publishes:
 *
 *   - **`version`** — MOB-03's cancel requires `expectedVersion`, so `Cancel for free` cannot be
 *     composed without reading the row it is cancelling. There is no published alternative: EXP-002's
 *     journey aggregate does not carry the request's version.
 *   - **`quote.product.id`** — the class the rider booked, which is the `XL` in
 *     `No XL drivers are free right now`. MOB-01's listing turns it into a display name, the same
 *     join `3 Quote` makes for `KES 2,100 · XL`.
 *   - **`quote.origin`** — the pickup, which GEO-02 turns into the `Westlands` in
 *     `Asking drivers near Westlands`.
 *   - **`quote.origin` and `quote.destination` together** — the two points MOB-02 needs to price the
 *     alternative class `5b No driver` offers.
 *
 * The embedded quote carries more than this — rate card identifiers, contract versions, a routing
 * model version — and only what a screen draws is restated, for the reason the header gives about
 * this file being the hand-written edge.
 */
export interface RideRequestRecord {
  readonly request: {
    readonly id: string;
    readonly tenantId: string;
    readonly passengerId: string;
    readonly quote: {
      readonly id: string;
      readonly product: { readonly id: string; readonly version: number };
      readonly route: { readonly distanceMetres: number; readonly durationSeconds: number };
      readonly fare: { readonly currency: string; readonly totalMinor: number };
      readonly origin: { readonly latitude: number; readonly longitude: number };
      readonly destination: { readonly latitude: number; readonly longitude: number };
      readonly createdAt: string;
      readonly expiresAt: string;
    };
    readonly status: "requested" | "cancelled";
    readonly version: number;
    readonly createdAt: string;
    readonly updatedAt: string;
    readonly cancellation?: {
      readonly reasonCode: string;
      readonly cancelledAt: string;
    };
  };
}

/** The five values DSP-04 publishes for a dispatch process's `status`, and no sixth. */
export type DispatchStatus =
  "finding_candidates" | "offering" | "assigning" | "completed" | "failed";

/**
 * DSP-04's dispatch process, which is the whole of `5 Finding` and `5b No driver`.
 *
 * `GET /v1/tenants/{tenantId}/dispatch-processes/{demandId}` under `dispatch:read`. Every field the
 * two screens draw was computed by DSP-04 from the day it was written and published only inside the
 * **202 of its event-ingestion `POST`**, behind `dispatch:orchestrate` — so the only caller that
 * could read a dispatch's progress was one entitled to drive the saga.
 * [ADR 0031](../../../docs/adr/0031-the-dispatch-process-is-read-under-its-own-scope-and-bounded-by-the-tenant-because-the-record-has-no-subject.md)
 * gave it a read of its own, and that one read moved both screens from `blocked` to `buildable`
 * with nothing left missing.
 *
 * The `demandId` **is** MOB-03's ride-request identifier — DSP-04's own parameter description says
 * so — which is why these screens are addressed by the same `?ride=` every journey screen carries
 * and need nothing new in the address.
 *
 * Bounded by the verified tenant rather than by the record, because the process row carries no
 * subject at all. A rider's own bound is MOB-03's `passengerId`, and there is deliberately **no
 * listing**: a caller reads a dispatch whose `demandId` it already holds. So a rider cannot
 * enumerate a tenant's dispatches, and this application never tries to.
 *
 * Six of the fifteen fields are nullable, and the nullability is the contract's rather than a
 * defensive guess: `requestedAt` is absent when the demand event carried none, and
 * `candidateBatchId`, `acceptedOfferId`, `assignmentId` and `failureReason` are each null until the
 * transition that sets them. `failureReason` null on a `failed` process would be a fault, and the
 * screen says so rather than drawing the design's sentence over nothing.
 */
export interface DispatchProcess {
  readonly processId: string;
  readonly tenantId: string;
  /** MOB-03's ride-request identifier, for a ride. */
  readonly demandId: string;
  readonly demandVersion: number;
  readonly status: DispatchStatus;
  /** When the search stops. The countdown on `5 Finding` is against this. */
  readonly deadlineAt: string;
  readonly requestedAt: string | null;
  /** Which attempt the search is on, one-based. The `1` in `try 1 of 3`. */
  readonly attempt: number;
  /** The attempt ceiling. The `3` in `try 1 of 3`, and the one field DSP-05 does not carry. */
  readonly maximumAttempts: number;
  readonly candidateBatchId: string | null;
  readonly acceptedOfferId: string | null;
  readonly assignmentId: string | null;
  /** `no_supply`, `deadline_exceeded` or `dispatch_failed`, set only on a `failed` process. */
  readonly failureReason: string | null;
  readonly version: number;
  /** When the orchestrator last moved this row, which is how stale an answer is measured. */
  readonly updatedAt: string;
}

/* ------------------------------------------- the second tranche: commerce, money and a payment */

/**
 * MER-01's merchant, narrowed to what `Merchants` draws.
 *
 * `status` is the only enumerated thing this record says about a merchant's standing, and the design
 * draws a trading state — `open`, `busy, longer prep`, `opens 14:00` — which is a different fact.
 * `Merchants.tsx` records what it does with the difference.
 */
export interface Merchant {
  readonly id: string;
  readonly tenantId: string;
  readonly legalName: string;
  readonly tradingName: string;
  readonly registrationNumber: string;
  readonly contactEmail: string;
  readonly status: "pending_review" | "active" | "suspended" | "closed";
  readonly statusReason?: string;
  readonly version: number;
  readonly createdAt: string;
  readonly updatedAt: string;
}

/** MER-02's branch: the name `Products · stock` puts in its chip and the one time it publishes. */
export interface MerchantLocation {
  readonly id: string;
  readonly tenantId: string;
  readonly merchantId: string;
  readonly branchCode: string;
  readonly name: string;
  readonly addressLabel: string;
  readonly timeZone: string;
  readonly fulfillmentModes: readonly ("pickup" | "merchant_delivery" | "courier_delivery")[];
  readonly preparationMinutes: number;
  readonly status: "active" | "inactive";
  readonly version: number;
}

/** COM-02's product, which is three of `Products · stock`'s four columns. */
export interface Product {
  readonly productId: string;
  readonly tenantId: string;
  readonly merchantId: string;
  readonly catalogId: string;
  readonly categoryId: string;
  readonly sku: string;
  readonly name: string;
  readonly description: string;
  readonly price: { readonly currency: string; readonly amountMinor: number };
  readonly status: "draft" | "active" | "suspended" | "retired";
  readonly version: number;
}

/**
 * COM-03's availability, which is the whole of the stock column and not enough for it.
 *
 * G-36, which `docs/design/DESIGN_RECONCILIATION.md` records as still open: the service answers a
 * count and when it was written, and **no freshness judgement**. The design's four states are
 * derived from the count, its age and its absence, which is what the design's own note says to do
 * until the service publishes one. DRV-05's `fresh` is the pattern the reconciliation names to copy.
 */
export interface Availability {
  readonly tenantId: string;
  readonly merchantId: string;
  readonly locationId: string;
  readonly sku: string;
  readonly availableQuantity: number;
  readonly version: number;
  readonly updatedAt: string;
  readonly updatedBy: string;
}

/** COM-01's catalogue, read for one thing: the category name `Fruit and veg` is drawn from. */
export interface Catalog {
  readonly catalogId: string;
  readonly tenantId: string;
  readonly merchantId: string;
  readonly name: string;
  readonly status: "draft" | "published" | "archived";
  readonly categories: readonly { readonly categoryId?: string; readonly name?: string }[];
  readonly version: number;
}

/** EXP-002's money, which is the shape every figure on the second tranche arrives in. */
export interface ApiMoney {
  readonly currency: string;
  readonly amountMinor: number;
}

/** One line of EXP-002's cart review: a cart item with its name and its stock looked up. */
export interface CartLine {
  readonly productId: string;
  readonly merchantLocationId: string;
  readonly quantity: number;
  readonly unitPrice: ApiMoney;
  readonly linePrice: ApiMoney;
  readonly name: string | null;
  readonly sku: string | null;
  readonly productAbsence?:
    "unreadable" | "forbidden" | "not_requested" | "not_found" | "not_this_merchant";
  readonly catalogueRepricedTo?: ApiMoney;
  readonly availableQuantity: number | null;
  readonly stockAbsence?:
    "unreadable" | "forbidden" | "not_requested" | "not_found" | "no_sku" | "not_this_location";
  readonly inStock: boolean | null;
}

/**
 * EXP-002's cart review, which is `Cart` and `Cart conflict` over one read.
 *
 * Three of its fields are typed `null` in the contract rather than nullable, which is the component
 * saying "there is nothing to publish here and there never will be until a service changes":
 * `itemProvenance` (which device added a line), `chargeableTotal` (the two fees), and both of their
 * `…Absence` reasons. They are the two elements the measurement names for these screens.
 */
export interface CartReview {
  readonly tenantId: string;
  readonly customerId: string;
  readonly cartId: string;
  readonly observedAt: string;
  readonly merchantId: string | null;
  readonly status: "active" | "expired" | null;
  readonly version: number | null;
  readonly itemProvenance: null;
  readonly itemProvenanceAbsence?: "no_published_field";
  readonly expiresAt: string | null;
  readonly expired: boolean;
  readonly lines: readonly CartLine[];
  readonly itemsTotal: ApiMoney | null;
  readonly chargeableTotal: null;
  readonly chargeableTotalAbsence?: "no_published_route" | "mixed_currency";
  readonly itemCount: number;
  readonly degraded: boolean;
}

/** COM-04's answer to a change of one line, and the 409 `Cart conflict` is drawn from. */
export interface CartAfterChange {
  readonly cart: {
    readonly id: string;
    readonly version: number;
    readonly status: "active" | "expired";
    readonly expiresAt: string;
  };
  readonly replayed: boolean;
}

/**
 * COM-05's placed order, as its 201 answers one.
 *
 * `subtotalAmountMinor` and `totalAmountMinor` are both published and this build draws neither as
 * a chargeable figure, because COM-05's domain requires them to be **equal**: it refuses any
 * order whose total is not the line snapshot's subtotal
 * (`src/domain/commerce-order.ts`: _"Order total must equal the immutable line snapshot
 * subtotal"_). So the fee-inclusive amount the design draws is not merely unpublished — the
 * command that would have carried it rejects it.
 */
export interface PlacedOrder {
  readonly orderId: string;
  readonly tenantId: string;
  readonly customerId: string;
  readonly merchantId: string;
  readonly currency: string;
  readonly subtotalAmountMinor: number;
  readonly totalAmountMinor: number;
  readonly status: string;
  readonly version: number;
}

/** One line of the immutable snapshot COM-05 stores, which is what `PlaceOrder` sends. */
export interface OrderLineToPlace {
  readonly productId: string;
  readonly sku: string;
  readonly name: string;
  readonly quantity: number;
  readonly unitAmountMinor: number;
}

/** COM-07's preparation, which is `Order tracking`'s step bar and its two times. */
export interface OrderPreparation {
  readonly orderId: string;
  readonly tenantId: string;
  readonly merchantId: string;
  readonly merchantLocationId: string;
  readonly status: "pending" | "preparing" | "ready";
  readonly version: number;
  readonly acceptedAt: string;
  readonly readyAt?: string;
  readonly updatedAt: string;
}

/* ------------------------------------------------ the fifth tranche: money, as FIN-01 and FIN-03
   publish it under ADR 0041, and the instruments PAY-04 publishes under ADR 0042 */

/**
 * FIN-01's wallet, as its public read answers one. Three fields, and **no balance**.
 *
 * `14 WP13 Money and Account.dc.html`'s own note says _"Wallet: FIN-01 balance with the FIN-03
 * authoritative flag"_, and that half of it is wrong about this platform: FIN-01's contract has said
 * since it was written that _"balances and postings are deliberately absent"_, and `AGENTS.md` is
 * why — a service never reads another's database, and a balance projection is not the accounting
 * source of truth. So this read supplies the `KES` in `KES 1,240.00` and whether the wallet can be
 * spent from, and `AccountBalances` below supplies the figure.
 */
export interface WalletSummary {
  readonly walletId: string;
  readonly currency: string;
  readonly status: "ACTIVE" | "RESTRICTED" | "FROZEN" | "CLOSED";
}

/**
 * One currency's projected balance, as FIN-03's public read answers it.
 *
 * `netDebitMinor` is the **ledger's** sign: debits positive. It is not an available balance and
 * FIN-03 deliberately does not turn it into one, because nothing on this platform publishes a chart
 * of accounts and so no service knows which side an account's normal balance sits on. `money.ts`'s
 * `walletBalanceMinor` is where this application converts, once, with its evidence written down and
 * Q-146 recording the assumption.
 *
 * `authoritativeAsOf` is the instant through which the projection had absorbed every posting the
 * ledger had issued. `checkpoint` is the ledger sequence that instant corresponds to, and is what
 * lets the screen ask FIN-02 which movements came after it — which is the whole of
 * `Order co_9A1B · not counted yet`.
 */
export interface ProjectedBalance {
  readonly currency: string;
  readonly netDebitMinor: number;
  readonly checkpoint: string;
  readonly authoritativeAsOf: string;
}

export interface AccountBalances {
  readonly balances: readonly ProjectedBalance[];
  /** `FIN-02` — which service the figure is a projection *of*. */
  readonly authority: string;
  /**
   * Always `false`, and the design names this flag out loud.
   *
   * _"We won't show a balance as exact until our ledger confirms it (FIN-03 authoritative flag)."_
   * FIN-03 cannot ever answer `true`: it learns of a posting by being handed one, so it has no way
   * to know whether FIN-02 has issued another.
   *
   * **`Wallet.tsx` does not read it**, and that is deliberate rather than an omission. The screen
   * reaches `balance confirmed 12:40` by comparing `checkpoint` against FIN-02's own listing, and
   * that comparison is strictly better informed than the flag: it is made against the ledger the
   * flag is about. A screen that also believed a `true` would be trusting a projection's opinion of
   * a service it cannot see, which is the thing FIN-03 is careful not to express. The field is
   * typed because the operation publishes it and the design names it, not because anything branches
   * on it.
   */
  readonly authoritative: boolean;
}

/**
 * One of FIN-02's journal transactions, as its public listing answers a row.
 *
 * The listing answers **every posting in the tenant** and offers `sourceType`, `currency`, `limit`
 * and `after` and no customer or account filter. So this application narrows by `lines.accountId` on
 * the client, against the account it is already drawing — which is what makes the rows this rider's
 * own. It is a narrowing and not a bound: the response that arrives has other people's postings in
 * it, and `Wallet.tsx` records that as the finding it is rather than as a detail.
 */
export interface JournalLine {
  readonly accountId: string;
  readonly side: "debit" | "credit";
  readonly amountMinor: number;
  readonly lineNumber: number;
}

export interface JournalTransaction {
  readonly id: string;
  readonly tenantId: string;
  readonly sequenceNumber: string;
  readonly sourceType: "payment" | "refund" | "reversal" | "commission" | "payout" | "adjustment";
  readonly sourceId: string;
  readonly currency: string;
  readonly postedAt: string;
  readonly effectiveAt: string;
  readonly lines: readonly JournalLine[];
  readonly correctionOfTransactionId?: string;
  readonly correctionReason?: string;
}

/**
 * PAY-04's stored mobile-money account, as its public listing answers one: masked, and only masked.
 *
 * `maskedAccount` arrives as `··· 0421` — the service's own rendering, elision included — and this
 * application concatenates it after the provider and shortens nothing. That is ADR 0042's whole
 * point: for a browser to shorten an account, the account would have had to reach the browser.
 */
export interface PaymentInstrument {
  readonly instrumentId: string;
  readonly provider: "M-Pesa" | "Airtel Money";
  readonly maskedAccount: string;
  readonly isDefault: boolean;
  readonly verification: "unverified" | "verified" | "rejected";
  readonly registeredAt: string;
}

/** SUP-01's support case, as its reads answer one. */
export interface SupportCaseRecord {
  readonly id: string;
  readonly tenantId: string;
  readonly customerReference: string;
  readonly subject: string;
  readonly category: string;
  readonly priority: string;
  readonly status: string;
  readonly version: number;
  readonly createdAt: string;
  readonly updatedAt: string;
}

/**
 * SUP-02's timeline entry, which publishes what happened and **not who wrote it**.
 *
 * `participantSubjects` is an access list rather than an author: it is who may read the entry,
 * and it is how the service bounds this read per entry. This application reads it for one thing
 * only — whether the rider is on it — because an entry naming this rider is this rider's.
 */
export interface TimelineEntryRecord {
  readonly tenantId: string;
  readonly caseId: string;
  readonly entryId: string;
  readonly source: string;
  readonly occurredAt: string;
  readonly category: string;
  readonly summary: string;
  readonly reference: string | null;
  readonly participantSubjects: readonly string[];
}

/** FIN-04's refund, as its single read answers one. */
export interface Refund {
  readonly tenantId: string;
  readonly refundId: string;
  readonly paymentIntentId: string;
  readonly amountMinor: number;
  readonly capturedAmountMinor: number;
  readonly previouslySucceededMinor: number;
  readonly currency: string;
  readonly reasonCode: string;
  readonly status:
    "REQUESTED" | "PROCESSING" | "SUCCEEDED" | "FAILED" | "CANCELLED" | "OUTCOME_UNKNOWN";
  readonly version: number;
  readonly requestedAt: string;
  readonly updatedAt: string;
  readonly completedAt?: string;
}

/**
 * One entry of CUS-04's consent history, which is the only thing CUS-04 publishes.
 *
 * Not a list of switches. The operation answers **every grant and withdrawal ever recorded**, in
 * `sequence` order, and whether a purpose is on now is the `kind` of its latest entry. That is the
 * design's own sentence — "Consent is a page, not a checkbox; each toggle is a versioned record with
 * history" — arriving as the contract rather than as a note.
 */
export interface ConsentEntry {
  readonly id: string;
  readonly tenantId: string;
  readonly customerId: string;
  readonly purpose: string;
  readonly purposeVersion: string;
  readonly policyVersion: string;
  readonly kind: "grant" | "withdrawal";
  readonly sequence: number;
  readonly supersedesGrantId: string;
  readonly evidenceReference: string;
  readonly source: string;
  readonly jurisdiction: string;
  readonly recordedAt: string;
}

/** CUS-03's preference, whose `key` is an enumeration and whose `value` is not. */
export interface CustomerPreference {
  readonly tenantId: string;
  readonly customerId: string;
  readonly key: string;
  readonly keyVersion: number;
  readonly value: string | boolean;
  readonly provenance: "explicit" | "inferred" | "tenant_default";
  readonly version: number;
  readonly updatedAt: string;
}

/** IAM-04's session, which is `Signed-in devices`. */
export interface IdentitySession {
  readonly id: string;
  readonly tenantId: string;
  readonly subjectId: string;
  readonly deviceId: string;
  readonly deviceName?: string;
  readonly platform?: string;
  readonly status: "active" | "revoked" | "expired";
  readonly lastSeenAt: string;
  readonly expiresAt: string;
  readonly version: number;
}

/**
 * EXP-002's payment outcome, which is all five screens of `07 Payment Outcome Unknown`.
 *
 * `outcome` is the field the whole deliverable exists for. It is taken from PAY-02's execution
 * **and** PAY-01's intent and never from one of them: PAY-02 `succeeded` over PAY-01 `processing` is
 * `unknown` rather than `paid`, because the intent is the record the rest of the platform settles
 * against. `unreadable` is a sixth thing and is not one of the five screens: it is this component
 * failing to find out, which the application draws as its own error state rather than as "we don't
 * know yet".
 *
 * Three of its fields are typed `null` in the contract, and each is a drawn element: `instrument`
 * (`mobile money ··· 0421` — no service on this platform models a stored instrument), `attempts`
 * (`Asked your provider 3 times · last at 21:15:40`), and `support` (`Get help`, whose only route is
 * under `/internal/v1`).
 */
export interface PaymentOutcomeView {
  readonly tenantId: string;
  readonly customerId: string;
  readonly executionId: string;
  readonly observedAt: string;
  readonly outcome: "unknown" | "unreadable" | "paid" | "not_charged" | "pending";
  readonly executionState: "dispatching" | "pending_confirmation" | "succeeded" | "declined" | null;
  readonly intentStatus: "requested" | "succeeded" | "processing" | "failed" | "canceled" | null;
  readonly amount: ApiMoney | null;
  readonly amountAbsence?:
    "execution_unreadable" | "payer_unconfirmed" | "not_this_payer" | "amount_disagrees";
  readonly method: "mobile_money" | "card" | "bank" | null;
  readonly instrument: null;
  readonly instrumentAbsence?: "no_published_service";
  readonly providerReference: string | null;
  readonly providerCode: string | null;
  readonly uncertaintyReason: string | null;
  readonly requestedAt: string | null;
  readonly silentForMs: number | null;
  readonly attempts: null;
  readonly attemptsAbsence?: "no_published_field";
  readonly reference: {
    readonly type: "trip" | "commerce_order" | "delivery_order" | "shipment";
    readonly id: string;
  } | null;
  readonly support: null;
  readonly supportAbsence?: "no_published_route";
  readonly degraded: boolean;
}

/**
 * One stop of TRP-04's plan, narrowed to the fields `Multi-stop in trip` draws.
 *
 * `addressId` is the field this screen exists on the far side of.
 * [ADR 0036](../../../docs/adr/0036-a-stop-names-an-address-and-trp-04-confirms-it.md) narrowed a
 * stop's place from a free-form `locationReference` to a UUID naming a GEO-02 address, and made
 * `POST /v1/tenants/{tenantId}/stop-plans` confirm every one of them before a plan is written — so
 * a plan that exists at all names an address that exists. That is what makes the second hop below
 * worth making: an address read answers a screen holding an identifier and is worth nothing to one
 * holding an opaque string.
 *
 * `arrivedAt` and `completedAt` are both nullable, and the design draws both of them in one
 * sentence — `Arrived 12:58 · left 13:02`. The contract has no `departedAt`: a stop's `completedAt`
 * is the instant the design calls "left", which is a reading recorded as a design question rather
 * than a published mapping.
 */
export interface StopPlanStop {
  readonly stopId: string;
  readonly sequence: number;
  readonly addressId: string;
  readonly instructions: string | null;
  readonly status: "pending" | "arrived" | "completed" | "skipped";
  readonly arrivedAt: string | null;
  readonly completedAt: string | null;
  readonly skipReason: string | null;
}

/** TRP-04's plan, which is the first of `Multi-stop in trip`'s two hops. */
export interface StopPlanView {
  readonly planId: string;
  readonly tenantId: string;
  readonly tripId: string;
  readonly tripVersion: number;
  readonly riderId: string;
  readonly driverId: string;
  readonly version: number;
  readonly stops: readonly StopPlanStop[];
  readonly createdAt: string;
}

/**
 * GEO-02's address record: the place an identifier names.
 *
 * `formattedAddress` is the only text on this platform that answers "where is this stop". The
 * design writes a stop's row as `Upper Hill, Hospital Rd` and the same stop's headline as
 * `Heading to Upper Hill` — two spellings of one place, and the platform publishes one. So both are
 * drawn from this field whole, which is also exactly what the measurement claims when it points
 * both of those elements at this read.
 *
 * `latitude` and `longitude` are what make a leg's duration askable: GEO-04 takes two coordinates,
 * and these are the only coordinates a stop has.
 */
export interface AddressView {
  readonly addressId: string;
  readonly tenantId: string;
  readonly formattedAddress: string;
  readonly countryCode: string;
  readonly locality?: string;
  readonly administrativeArea?: string;
  readonly postalCode?: string;
  readonly latitude: number;
  readonly longitude: number;
  readonly accuracyMetres: number;
  readonly precision: "rooftop" | "street" | "locality" | "unknown";
  readonly provider: string;
  readonly language: string;
  readonly resolvedAt: string;
  readonly version: number;
  readonly createdAt: string;
  readonly updatedAt: string;
}

/* ------------------------------------------------------------------------------ the seam itself */

/**
 * One trip a rider took, as TRP-01's own listing answers it.
 *
 * `fare`, `pickupLabel` and `dropoffLabel` are the three fields ADR 0096's sibling migration added
 * to the trip, and they are the reason `Order history` can be drawn at all. The screen waited on a
 * decision about where a trip's fare and addresses live — a fan-out to MOB-03 and MOB-02 would
 * have cost two calls per row and shown the quote **as it is now** rather than what the rider was
 * charged, because neither read takes a version and TRP-01 pins `quoteVersion` deliberately. The
 * trip records what it cost, so a history reads one listing and shows the figure that was charged.
 *
 * All three stay optional. A trip created before that migration carries none of them, and a row
 * for such a trip is drawn as an absence rather than left out — a history that silently omits the
 * journeys it cannot price is worse than one that says which it cannot.
 */
export interface OwnTrip {
  readonly tripId: string;
  readonly status:
    | "assigned"
    | "driver_en_route"
    | "driver_arrived"
    | "waiting"
    | "in_trip"
    | "completed"
    | "cancelled"
    | "no_show";
  readonly fare?: { readonly amountMinor: string; readonly currency: string };
  readonly pickupLabel?: string;
  readonly dropoffLabel?: string;
  readonly createdAt: string;
}

/**
 * One commerce order, as COM-05's own listing answers it.
 *
 * `merchantId` and no merchant **name**: the design's `Kilimani Fresh` names the shop, and COM-05
 * publishes the identifier it was placed against. MER-01 holds the name, so drawing it would be a
 * second call per row against a service this screen does not otherwise read, and a listing cannot
 * make one call per row. The row is named by its absence instead.
 */
export interface OwnCommerceOrder {
  readonly orderId: string;
  readonly merchantId: string;
  readonly currency: string;
  readonly totalAmountMinor: number;
  readonly status: "placed" | "cancelled" | "completed";
  readonly placedAt: string;
}

/**
 * One delivery order, as DEL-03's own listing answers it.
 *
 * The thinnest of the three, and the row the design can least be drawn from. DEL-03 publishes
 * `deliveryAddressId` rather than a label, so `Parcel to Oyster Bay` has no name to use, and it
 * publishes **no money at all** — no total, no currency — so `TZS 8,000` has no source either.
 * Both are drawn as absences with the reason, which is what R-35 asks for and what keeps a parcel
 * from appearing to have cost nothing.
 */
export interface OwnDeliveryOrder {
  readonly orderId: string;
  readonly deliveryAddressId: string;
  readonly status:
    | "accepted"
    | "pickup_ready"
    | "in_custody"
    | "delivery_attempted"
    | "delivered"
    | "cancelled"
    | "failed";
  readonly createdAt: string;
}

/**
 * The window a history is read over, which all three listings take and none paginates.
 *
 * `from` and `to` and nothing else. This carried an optional `limit` and the builder below said
 * "`limit` is optional on all three", which was not true of any of them: TRP-01, DEL-03 and
 * COM-05 each publish exactly these two parameters. COM-05 validates its query strictly and
 * answered the deployed `rider · Order history` screen
 * `request_validation_failed … unrecognized_keys: ["limit"]`, so the screen reported "no source
 * answered" with all three services running and two of them willing.
 *
 * It is the shape AGENTS.md and CLAUDE.md both name: a field the contract does not publish is a
 * contract gap to record, never a parameter to send and hope for.
 */
export interface HistoryWindow {
  readonly from: string;
  readonly to: string;
}

export interface RiderApi {
  /** EXP-002's journey aggregate: eight services behind one screen, seven screens over one read. */
  rideJourney(input: {
    readonly tenantId: string;
    readonly customerId: string;
    readonly rideRequestId: string;
    readonly tripId?: string;
  }): Promise<JourneyView>;

  /** GEO-02, for `1 Where to`'s current position and `3 Quote`'s two endpoints. */
  reverseGeocode(input: {
    readonly latitude: number;
    readonly longitude: number;
    readonly accuracyMeters: number;
  }): Promise<ReverseGeocodeAnswer>;

  /** GEO-01, for `Search destination`. */
  geocode(input: {
    readonly text: string;
    readonly limit: number;
    readonly proximity?: { readonly latitude: number; readonly longitude: number };
  }): Promise<GeocodeAnswer>;

  /** GEO-04, for `24 km · about 38 min`. */
  distanceEta(input: {
    readonly origin: { readonly latitude: number; readonly longitude: number };
    readonly destination: { readonly latitude: number; readonly longitude: number };
    readonly mode: TravelMode;
  }): Promise<DistanceEtaAnswer>;

  /** MOB-01's listing, which is the three rows of `2 Choose`. */
  rideProducts(): Promise<readonly RideProduct[]>;

  /** MOB-02's price. A write, because a quote is an immutable priced resource. */
  createQuote(
    input: {
      readonly productId: string;
      readonly origin: { readonly latitude: number; readonly longitude: number };
      readonly destination: { readonly latitude: number; readonly longitude: number };
    },
    idempotencyKey: string,
  ): Promise<RideQuote>;

  /** MOB-02's re-read of one, which is how `3 Quote` learns whether the price is still held. */
  validateQuote(quoteId: string): Promise<QuoteValidation>;

  /** MOB-03's booking command, under the key `4 Confirming` mints. */
  bookRide(quoteId: string, idempotencyKey: string): Promise<RideRequestAnswer>;

  /**
   * MOB-03's own read of a ride request, under ADR 0028's rider-side scope.
   *
   * Read by `5 Finding` and `5b No driver` for the four facts `RideRequestRecord` lists, the first
   * of which is the `expectedVersion` the cancel below cannot be composed without.
   */
  rideRequest(rideRequestId: string): Promise<RideRequestRecord>;

  /** MOB-03's cancel, which `3 Quote`'s free window and the search screens press. */
  cancelRide(input: {
    readonly rideRequestId: string;
    readonly reasonCode: string;
    readonly expectedVersion: number;
    readonly idempotencyKey: string;
  }): Promise<void>;

  /**
   * DSP-04's dispatch process, which is the whole of `5 Finding` and `5b No driver`.
   *
   * Called directly rather than through EXP-002, which is the same shape of decision ADR 0026
   * section 1 took about the class chooser and for a simpler reason: **EXP-002 does not serve it.**
   * Its journey aggregate still answers `dispatch: null` with `dispatchAbsence: no_published_route`,
   * which was true when the aggregate was written and is not any more. One read of one record under
   * one scope is not the fan-out WEB-0743 forbids, and EXP-003's own rule — a screen that reads one
   * record reads it directly — is what the two search screens do.
   *
   * No `Idempotency-Key`: it is a read, and the contract does not mark it idempotent.
   */
  dispatchProcess(input: {
    readonly tenantId: string;
    /** MOB-03's ride-request identifier, which DSP-04 calls the demand. */
    readonly demandId: string;
  }): Promise<DispatchProcess>;

  /** RAT-01's rating, from `8 Complete`'s five stars. */
  submitRating(input: RatingSubmission, idempotencyKey: string): Promise<void>;

  /* -------------------------------------------- the second tranche, in the design's own order */

  /** MER-01's listing, which is every row of `Merchants`. */
  merchants(input?: { readonly limit?: number }): Promise<readonly Merchant[]>;

  /** MER-01's one merchant, for the trading name three other screens put in their app bar. */
  merchant(merchantId: string): Promise<Merchant>;

  /**
   * MER-02's one branch, which is where `20–30 min` lives.
   *
   * **Addressed by a `locationId` nothing publishes a listing of.** MER-02 has a create, a read, a
   * patch and a status command, and no collection — so a screen cannot get from a merchant to its
   * branches, which is why `Merchants` draws no preparation time and `Products · stock` takes its
   * branch from the address. Recorded as a reconciliation finding.
   */
  merchantLocation(locationId: string): Promise<MerchantLocation>;

  /** COM-01's catalogue, read for the category name `Products · stock` heads its list with. */
  catalog(input: { readonly tenantId: string; readonly catalogId: string }): Promise<Catalog>;

  /** COM-02's listing, narrowed to one catalogue's category, which is `Products · stock`'s rows. */
  products(input: {
    readonly tenantId: string;
    readonly catalogId?: string;
    readonly categoryId?: string;
    readonly merchantId?: string;
    readonly limit?: number;
  }): Promise<readonly Product[]>;

  /**
   * COM-03's count for one SKU at one branch.
   *
   * One request per row, which is the shape of the gap rather than a choice: COM-03 is addressed by
   * a SKU and publishes no listing, so a page of eight products is eight reads. EXP-002's
   * `cart-reviews` bounds exactly this sweep on the cart and publishes how far it reached; nothing
   * does it for a product list, so `Products.tsx` bounds it itself and says so.
   */
  availability(input: {
    readonly merchantId: string;
    readonly locationId: string;
    readonly sku: string;
  }): Promise<Availability>;

  /** EXP-002's cart review: COM-04's cart with every line named and its stock checked. */
  cartReview(input: {
    readonly tenantId: string;
    readonly customerId: string;
    readonly cartId: string;
  }): Promise<CartReview>;

  /**
   * COM-04's change to one line, under the key the contract **requires** rather than marks.
   *
   * `expectedVersion` is the whole of `Cart conflict`: COM-04 answers 409 when the cart has moved
   * on, which is the designed state rather than an error to swallow. The key and the version do
   * different jobs and both are needed — the key makes a retry of the same change safe, the version
   * makes a change against a stale cart refused.
   */
  changeCartLine(
    input: {
      readonly cartId: string;
      readonly productId: string;
      readonly merchantLocationId: string;
      readonly quantity: number;
      readonly unitAmountMinor: number;
      readonly currency: string;
      readonly expectedVersion: number;
    },
    idempotencyKey: string,
  ): Promise<CartAfterChange>;

  /**
   * COM-05's order command, which `Checkout` presses and which was unreachable until now.
   *
   * This operation published a `requestBody` referencing `#/components/schemas/PlaceOrder`, a
   * schema its own contract never defined — so the one command the commerce flow exists to reach
   * described nothing a caller could send, and every screen downstream of it was graded blocked.
   * The shape was never missing: it was one branch of a `z.discriminatedUnion` held in a
   * module-local `const`, and the schema generator publishes exports whose names end in `Schema`.
   *
   * Two of its required fields are why `Checkout` can refuse before sending. Every line must carry
   * a `sku` and a `name`, and EXP-002 types both `string | null` — a line COM-02 could not name, or
   * whose SKU it did not answer, cannot be snapshotted, and this build says so rather than sending
   * a placeholder. `totalAmountMinor` must equal the sum of the lines; see `PlacedOrder`.
   */
  placeCommerceOrder(
    input: {
      readonly tenantId: string;
      readonly customerId: string;
      readonly merchantId: string;
      readonly merchantLocationId: string;
      readonly currency: string;
      readonly lines: readonly OrderLineToPlace[];
      readonly totalAmountMinor: number;
    },
    idempotencyKey: string,
  ): Promise<PlacedOrder>;

  /** COM-07's preparation, which is `Order tracking`. */
  orderPreparation(orderId: string): Promise<OrderPreparation>;

  /**
   * SUP-01's cases for this customer, which is how the support screen finds one at all.
   *
   * The customer is derived from the verified subject inside the service, and there is no
   * parameter by which this application could ask for somebody else's. ADR 0078.
   */
  supportCases(tenantId: string): Promise<readonly SupportCaseRecord[]>;
  /** SUP-01's one case, bounded to its customer by the stored reference. */
  supportCase(input: {
    readonly tenantId: string;
    readonly caseId: string;
  }): Promise<SupportCaseRecord>;
  /**
   * SUP-02's timeline for one case, bounded **per entry** by its participant list.
   *
   * The service refuses a subject who is on none of them, so what this read answers is decided
   * by the data rather than by the scope — which is why one scope serves both audiences.
   */
  supportTimeline(input: {
    readonly tenantId: string;
    readonly caseId: string;
  }): Promise<readonly TimelineEntryRecord[]>;

  /** FIN-04's one refund, addressed by its own identifier. See `Refund.tsx` for why not the list. */
  /**
   * The three listings behind `Order history`, each bounded by the token's own subject.
   *
   * Three calls and not one: there is no aggregate over a customer's whole activity, and ADR 0097
   * declined to build one because a BFF exists for a read a channel cannot make. A phone can make
   * three parallel reads perfectly well, and the screen reports each source separately so one
   * being down shows as one missing section rather than an empty history.
   *
   * None takes a cursor. All three are windowed — `from`, `to`, `limit` — and answer `nextAfter`
   * as null, so a caller that fills a page narrows the window instead of paging.
   */
  ownTrips(window: HistoryWindow): Promise<readonly OwnTrip[]>;

  /** COM-05's own-orders listing. See `ownTrips`. */
  ownCommerceOrders(window: HistoryWindow): Promise<readonly OwnCommerceOrder[]>;

  /** DEL-03's own-orders listing. See `ownTrips`. */
  ownDeliveryOrders(window: HistoryWindow): Promise<readonly OwnDeliveryOrder[]>;

  refund(refundId: string): Promise<Refund>;

  /** CUS-04's whole consent history, which is what `Consent` derives its four switches from. */
  consents(input: {
    readonly tenantId: string;
    readonly customerId: string;
  }): Promise<readonly ConsentEntry[]>;

  /** CUS-04's withdrawal, which is the designed toggle turning off. */
  withdrawConsent(input: {
    readonly tenantId: string;
    readonly customerId: string;
    readonly grantId: string;
    readonly evidenceReference: string;
    readonly jurisdiction: string;
  }): Promise<ConsentEntry>;

  /** CUS-04's grant, which is the same toggle turning back on. */
  grantConsent(input: {
    readonly tenantId: string;
    readonly customerId: string;
    readonly purpose: string;
    readonly purposeVersion: string;
    readonly policyVersion: string;
    readonly evidenceReference: string;
    readonly jurisdiction: string;
  }): Promise<ConsentEntry>;

  /** CUS-03's preferences, which are three of `Settings`'s five rows. */
  preferences(customerId: string): Promise<readonly CustomerPreference[]>;

  /** IAM-04's sessions for one subject, which is `Signed-in devices · 3 devices`. */
  sessions(subjectId: string): Promise<readonly IdentitySession[]>;

  /** IAM-04's revocation of one session, which signs one other device out. */
  revokeSession(input: {
    readonly sessionId: string;
    readonly reason: string;
  }): Promise<IdentitySession>;

  /** EXP-002's payment outcome: PAY-02's execution and PAY-01's intent, read as one answer. */
  paymentOutcome(input: {
    readonly tenantId: string;
    readonly customerId: string;
    readonly executionId: string;
  }): Promise<PaymentOutcomeView>;

  /** SAF-01's incident: the red SOS, and `6 Driver coming`'s Share trip. */
  raiseSafetyIncident(input: {
    readonly tenantId: string;
    readonly tripId: string;
    readonly messageId: string;
    readonly command:
      | { readonly type: "open_sos"; readonly reasonCode?: string }
      | {
          readonly type: "set_sharing";
          readonly enabled: boolean;
          readonly contactAliases?: readonly string[];
        };
  }): Promise<void>;

  /* ----------------------------------------- the third tranche: a trip with more than one stop */

  /**
   * TRP-04's stop plan, which is the whole subject of `Multi-stop in trip`.
   *
   * Read directly rather than through EXP-002, for the reason EXP-003's own rule gives and ADR 0026
   * section 1 took for the class chooser: a screen that reads one record reads it directly, and
   * EXP-002's journey aggregate does not serve a stop plan at all. One read of one record under one
   * scope is not the fan-out WEB-0743 forbids.
   *
   * No `Idempotency-Key`: it is a read, and the contract does not mark it idempotent.
   */
  stopPlan(input: { readonly tenantId: string; readonly planId: string }): Promise<StopPlanView>;

  /**
   * GEO-02's address, by the identifier a stop names. The second hop, and the reason this screen is
   * buildable.
   *
   * One request per **distinct** address rather than per stop, which is the same bound TRP-04's own
   * create command applies for the reason ADR 0036 section 2 gives: two stops at one doorway are one
   * question, and a twenty-stop plan must not be a twenty-fold burst on a provider-backed service.
   * The caller does the de-duplication because only the caller knows the plan.
   */
  address(addressId: string): Promise<AddressView>;

  /* -------------------------------------- the fifth tranche: money and the instruments behind it */

  /**
   * FIN-01's wallet, under ADR 0041's `wallet:self`.
   *
   * Read directly rather than through EXP-002, for the reason stated for the stop plan above and for
   * the one ADR 0026 section 1 gives: a screen that reads one record reads it directly, and EXP-002's
   * journey aggregate serves no wallet. Two of these three money reads are one record each and the
   * third is one listing, so there is no composition for a BFF to do — which is a smaller claim than
   * it sounds, because the two wallet screens make **three** reads across three services and that
   * fan-out is exactly what EXP-002 would eventually absorb.
   *
   * There is deliberately no `requestTopUp` beside it. `POST /v1/wallets/{walletId}/top-ups` exists
   * and takes an amount and a currency, and `14 WP13 Money and Account.dc.html` draws the `Top up`
   * button and **no amount entry anywhere** — so this application has nothing to send. Inventing a
   * form would be adding a screen the design team has not approved, on a money path, which
   * `CLAUDE.md` section 1 forbids outright. The button is drawn and says so when pressed, and Q-148
   * carries the question.
   */
  wallet(walletId: string): Promise<WalletSummary>;

  /**
   * FIN-03's projected balances for one account, under ADR 0041's `balance:projection:read`.
   *
   * Answers every currency the account holds, so this read requires nothing beyond the identifier —
   * the internal surface's required `currency` parameter is deliberately not on the public one.
   */
  accountBalances(input: {
    readonly tenantId: string;
    readonly accountId: string;
  }): Promise<AccountBalances>;

  /**
   * FIN-02's journal transactions, which are the `Recent` rows on both wallet screens.
   *
   * `after` and `limit` are the contract's own cursor; `currency` narrows the page to the wallet's.
   * There is no account or customer filter and this method does not pretend otherwise — the caller
   * narrows by `lines.accountId` and `Wallet.tsx` records what arrives before it does.
   */
  journalTransactions(input: {
    readonly currency: string;
    readonly limit: number;
  }): Promise<readonly JournalTransaction[]>;

  /**
   * PAY-04's own instruments for the calling subject, under ADR 0042's
   * `mobile-money:instruments:self`.
   *
   * No parameter names the owner, and that is the operation's design rather than this method's: the
   * listing is bounded by the record's `owner_subject` against the verified token, so there is
   * nothing for a caller to supply and nothing for it to get wrong.
   */
  paymentInstruments(tenantId: string): Promise<readonly PaymentInstrument[]>;
}

/** Every field RAT-01 requires, which is more than the design's five stars imply. */
export interface RatingSubmission {
  /** Minted by this application. RAT-01 requires it and publishes no route that allocates one. */
  readonly ratingId: string;
  readonly transactionId: string;
  readonly raterId: string;
  readonly subjectId: string;
  readonly score: number;
  readonly submittedAt: string;
}

/**
 * The version of the rating rules this application claims to have rated under.
 *
 * RAT-01 requires `ratingRuleVersion` and types it as a free string, publishing no set of values and
 * no operation that answers "which version is in force". The field is published and required — so
 * this is not an invented field — but the **value** is this build's, which is the same shape of
 * decision `apps/driver-app/src/driver-api.ts` records for DRV-05's `reasonCode`. It is named here
 * where it can be found and changed rather than written at a call site, and recorded as a design
 * question.
 */
export const RATING_RULE_VERSION = "v1";

/**
 * The reason code this application sends when a rider cancels.
 *
 * MOB-03 requires `reasonCode` on its cancel and types it as a free string, publishing no set of
 * values. Same shape as `RATING_RULE_VERSION` above, same reason for being here.
 */
export const CANCELLED_BY_RIDER = "rider_cancelled";

/**
 * What this build writes into CUS-04's `source` on a consent change.
 *
 * CUS-04 requires `source`, `jurisdiction` and `evidenceReference` on both a grant and a withdrawal
 * and types all three as free strings, publishing no vocabulary for any of them. Two of the three
 * can be answered from the record being changed: a withdrawal of a grant is in the **same**
 * jurisdiction that grant was recorded under, and CUS-04 publishes it on every entry. `source` is
 * the one this build has to choose, so it is named here beside `RATING_RULE_VERSION` and
 * `CANCELLED_BY_RIDER` rather than written at a call site, and it is recorded as a design question.
 *
 * `evidenceReference` is minted per change rather than fixed, by `consentEvidence` below: it is
 * meant to point at evidence of the act, and one constant for every consent change ever recorded
 * would point at nothing.
 */
export const CONSENT_SOURCE = "rider-app";

/**
 * The reference this build writes as CUS-04's `evidenceReference`.
 *
 * The screen the change was made on and the instant it was made, which is what this application
 * knows about the act. It is not a pointer into an evidence store, because there is no evidence
 * store published — and a reference that looked like one would be worse than a reference that
 * plainly says where it came from.
 */
export function consentEvidence(at: string): string {
  return `rider-app/consent@${at}`;
}

/**
 * The reason this build writes into IAM-04's revocation.
 *
 * Required and a free string, same shape as the three above. `user_signed_out_everywhere` is the
 * value `docs/design/DESIGN_RECONCILIATION.md` records the console's own sign-out dialog as using,
 * so the two channels write the same word for the same act rather than two.
 */
export const SESSION_REVOKED_BY_USER = "user_signed_out_everywhere";

/** How RAT-01 names the two sides of a trip rating. Enumerated by its own contract. */
const RATING_TRANSACTION_TYPE = "TRIP";
const RATING_RATER_TYPE = "CUSTOMER";
const RATING_SUBJECT_TYPE = "DRIVER";

/**
 * The query the three history listings share.
 *
 * `from` and `to` are required by all three and are the only two parameters any of them
 * publishes, so one builder rather than three. Built with `URLSearchParams` so a label with a
 * space or an ampersand in it cannot end the query early.
 *
 * Nothing else may be added here without the operation publishing it. This function used to set
 * a `limit`, and COM-05 rejects an unrecognised key rather than ignoring it — which is the
 * correct behaviour and is what turned one invented parameter into an empty screen.
 */
function historyQuery(window: HistoryWindow): string {
  const query = new URLSearchParams({ from: window.from, to: window.to });
  return `?${query.toString()}`;
}

export function createRiderApi(transport: Transport): RiderApi {
  return {
    rideJourney(input): Promise<JourneyView> {
      return transport.request({
        method: "GET",
        path: `/v1/tenants/${encodeURIComponent(input.tenantId)}/customer-app/customers/${encodeURIComponent(input.customerId)}/ride-journeys/${encodeURIComponent(input.rideRequestId)}`,
        query: {
          /*
           * The one identifier on this operation the caller supplies, and the reason seven screens
           * carry it in the address.
           *
           * R-28: **nothing published joins a ride request to the trip created from it.** TRP-01's
           * listing is behind `trip:workload:read`, TRP-02's behind `trip-tracking:operations`, and
           * DSP-03's offers only `status` and `driverId` — not one of which a rider holds. Omitted,
           * EXP-002 answers `tripAbsence: not_joinable` and the stage is never later than
           * `requested`, which is the honest answer and is deliberately inconvenient.
           */
          ...(input.tripId === undefined ? {} : { tripId: input.tripId }),
        },
      }) as Promise<JourneyView>;
    },

    reverseGeocode(input): Promise<ReverseGeocodeAnswer> {
      return transport.request({
        method: "POST",
        path: "/v1/reverse-geocode",
        body: {
          latitude: input.latitude,
          longitude: input.longitude,
          /*
           * Required by GEO-02, and passed through from the browser rather than defaulted.
           *
           * A reverse geocode of a fix with a 2 km radius is a different question from one with a
           * 5 m radius, and the service is told which it is being asked. Defaulting it here would be
           * this application telling GEO-02 how good a position it had.
           */
          accuracyMeters: input.accuracyMeters,
        },
      }) as Promise<ReverseGeocodeAnswer>;
    },

    geocode(input): Promise<GeocodeAnswer> {
      return transport.request({
        method: "POST",
        path: "/v1/geocode",
        body: {
          text: input.text,
          limit: input.limit,
          ...(input.proximity === undefined ? {} : { proximity: input.proximity }),
        },
      }) as Promise<GeocodeAnswer>;
    },

    distanceEta(input): Promise<DistanceEtaAnswer> {
      return transport.request({
        method: "POST",
        path: "/v1/distance-eta",
        body: { origin: input.origin, destination: input.destination, mode: input.mode },
      }) as Promise<DistanceEtaAnswer>;
    },

    async rideProducts(): Promise<readonly RideProduct[]> {
      const answer = (await transport.request({
        method: "GET",
        path: "/v1/ride-products",
      })) as { readonly products?: readonly RideProduct[] };
      /*
       * `?? []` rather than trusting the field, and the rows above it say so.
       *
       * An answer with no `products` key is not an empty catalogue — it is an answer this
       * application could not read — and the two are drawn differently. `2 Choose` carries the
       * distinction in its own empty state rather than here.
       */
      return answer.products ?? [];
    },

    async createQuote(input, idempotencyKey): Promise<RideQuote> {
      const answer = (await transport.request({
        method: "POST",
        path: "/v1/ride-quotes",
        body: {
          productId: input.productId,
          origin: input.origin,
          destination: input.destination,
        },
        // Required by the contract, not optional. MOB-02 refuses the request without it.
        options: { idempotencyKey },
      })) as { readonly quote: RideQuote };
      return answer.quote;
    },

    validateQuote(quoteId): Promise<QuoteValidation> {
      // No key: the contract does not mark this idempotent, and it is a read of a price's standing
      // rather than a command. `CLAUDE.md` section 2's "a check status action is a read".
      return transport.request({
        method: "POST",
        path: `/v1/ride-quotes/${encodeURIComponent(quoteId)}/validate`,
      }) as Promise<QuoteValidation>;
    },

    bookRide(quoteId, idempotencyKey): Promise<RideRequestAnswer> {
      /*
       * The one command on this application that cannot be undone without a cancellation, and the
       * reason `4 Confirming` exists as a designed screen at all: its own note says _"If the
       * connection drops we'll check what happened instead of booking twice."_ The key is minted on
       * the quote screen and reused on every retry of the same booking, which is what makes that
       * sentence true rather than reassuring.
       */
      return transport.request({
        method: "POST",
        path: "/v1/ride-requests",
        body: { quoteId },
        options: { idempotencyKey },
      }) as Promise<RideRequestAnswer>;
    },

    rideRequest(rideRequestId): Promise<RideRequestRecord> {
      return transport.request({
        method: "GET",
        path: `/v1/ride-requests/${encodeURIComponent(rideRequestId)}`,
      }) as Promise<RideRequestRecord>;
    },

    async cancelRide(input): Promise<void> {
      await transport.request({
        method: "POST",
        path: `/v1/ride-requests/${encodeURIComponent(input.rideRequestId)}/cancel`,
        body: { reasonCode: input.reasonCode, expectedVersion: input.expectedVersion },
        options: { idempotencyKey: input.idempotencyKey },
      });
    },

    async dispatchProcess(input): Promise<DispatchProcess> {
      const answer = (await transport.request({
        method: "GET",
        path: `/v1/tenants/${encodeURIComponent(input.tenantId)}/dispatch-processes/${encodeURIComponent(input.demandId)}`,
      })) as { readonly process: DispatchProcess };
      return answer.process;
    },

    async submitRating(input, idempotencyKey): Promise<void> {
      await transport.request({
        method: "POST",
        path: "/v1/ratings",
        body: {
          ratingId: input.ratingId,
          transactionType: RATING_TRANSACTION_TYPE,
          transactionId: input.transactionId,
          raterType: RATING_RATER_TYPE,
          raterId: input.raterId,
          subjectType: RATING_SUBJECT_TYPE,
          subjectId: input.subjectId,
          score: input.score,
          ratingRuleVersion: RATING_RULE_VERSION,
          submittedAt: input.submittedAt,
        },
        options: { idempotencyKey },
      });
    },

    /* ----------------------------------------------------------------- the second tranche */

    async merchants(input = {}): Promise<readonly Merchant[]> {
      const answer = (await transport.request({
        method: "GET",
        path: "/v1/merchants",
        query: {
          /*
           * No `status` filter, deliberately.
           *
           * MER-01's `status` is a lifecycle — `pending_review | active | suspended | closed` — and
           * the design's rows are a **trading** state: `open`, `busy, longer prep`,
           * `opens 14:00`. Filtering to `active` would hide a suspended merchant from a rider
           * without saying so, which is the opposite of what this application does with an absence.
           * The screen draws every merchant the tenant has and shows each one's published standing.
           */
          ...(input.limit === undefined ? {} : { limit: String(input.limit) }),
        },
      })) as { readonly merchants?: readonly Merchant[] };
      // `?? []` rather than trusting the field, for the reason `rideProducts` states.
      return answer.merchants ?? [];
    },

    async merchant(merchantId): Promise<Merchant> {
      const answer = (await transport.request({
        method: "GET",
        path: `/v1/merchants/${encodeURIComponent(merchantId)}`,
      })) as { readonly merchant: Merchant };
      return answer.merchant;
    },

    async merchantLocation(locationId): Promise<MerchantLocation> {
      const answer = (await transport.request({
        method: "GET",
        path: `/v1/merchant-locations/${encodeURIComponent(locationId)}`,
      })) as { readonly merchantLocation: MerchantLocation };
      return answer.merchantLocation;
    },

    async catalog(input): Promise<Catalog> {
      const answer = (await transport.request({
        method: "GET",
        path: `/v1/tenants/${encodeURIComponent(input.tenantId)}/catalogs/${encodeURIComponent(input.catalogId)}`,
      })) as { readonly catalog: Catalog };
      return answer.catalog;
    },

    async products(input): Promise<readonly Product[]> {
      const answer = (await transport.request({
        method: "GET",
        path: `/v1/tenants/${encodeURIComponent(input.tenantId)}/products`,
        query: {
          ...(input.catalogId === undefined ? {} : { catalogId: input.catalogId }),
          ...(input.categoryId === undefined ? {} : { categoryId: input.categoryId }),
          ...(input.merchantId === undefined ? {} : { merchantId: input.merchantId }),
          /*
           * `status: active`, which is **not** the same decision as the merchant listing above.
           *
           * A retired product is not a product with a different standing; it is one the merchant has
           * withdrawn from sale, and COM-02's own enumeration says so. The design's list is
           * `Fruit and veg` — what can be bought — and its four stock states are all about a product
           * that is on sale. A drawn `draft` row would be an item no rider can order.
           */
          status: "active",
          ...(input.limit === undefined ? {} : { limit: String(input.limit) }),
        },
      })) as { readonly products?: readonly Product[] };
      return answer.products ?? [];
    },

    async availability(input): Promise<Availability> {
      const answer = (await transport.request({
        method: "GET",
        path: `/v1/merchants/${encodeURIComponent(input.merchantId)}/locations/${encodeURIComponent(input.locationId)}/inventory/${encodeURIComponent(input.sku)}`,
      })) as { readonly availability: Availability };
      return answer.availability;
    },

    cartReview(input): Promise<CartReview> {
      return transport.request({
        method: "GET",
        path: `/v1/tenants/${encodeURIComponent(input.tenantId)}/customer-app/customers/${encodeURIComponent(input.customerId)}/cart-reviews/${encodeURIComponent(input.cartId)}`,
      }) as Promise<CartReview>;
    },

    changeCartLine(input, idempotencyKey): Promise<CartAfterChange> {
      return transport.request({
        method: "PUT",
        path: `/v1/carts/${encodeURIComponent(input.cartId)}/items`,
        body: {
          productId: input.productId,
          merchantLocationId: input.merchantLocationId,
          quantity: input.quantity,
          unitAmountMinor: input.unitAmountMinor,
          currency: input.currency,
          expectedVersion: input.expectedVersion,
        },
        // Required by the contract, not optional: COM-04 refuses the request without it.
        options: { idempotencyKey },
      }) as Promise<CartAfterChange>;
    },

    async placeCommerceOrder(input, idempotencyKey): Promise<PlacedOrder> {
      const answer = (await transport.request({
        method: "POST",
        path: `/v1/tenants/${encodeURIComponent(input.tenantId)}/commerce-orders`,
        body: {
          // The discriminator. COM-05's body is a union and `place` is the branch this is.
          type: "place",
          customerId: input.customerId,
          merchantId: input.merchantId,
          merchantLocationId: input.merchantLocationId,
          currency: input.currency,
          lines: input.lines,
          totalAmountMinor: input.totalAmountMinor,
          /*
           * When the rider pressed, not when the cart was read.
           *
           * COM-05 validates this timestamp and stores it on the immutable snapshot, so it is the
           * order's own moment. Sending the cart's `observedAt` would date the order to a read
           * that may have happened minutes earlier.
           */
          occurredAt: new Date().toISOString(),
        },
        // Marked idempotent by the contract: `#/components/parameters/IdempotencyKey`.
        options: { idempotencyKey },
      })) as { readonly order: PlacedOrder };
      return answer.order;
    },

    async orderPreparation(orderId): Promise<OrderPreparation> {
      const answer = (await transport.request({
        method: "GET",
        path: `/v1/order-preparations/${encodeURIComponent(orderId)}`,
      })) as { readonly orderPreparation: OrderPreparation };
      return answer.orderPreparation;
    },

    async supportCases(tenantId): Promise<readonly SupportCaseRecord[]> {
      const answer = (await transport.request({
        method: "GET",
        path: `/v1/tenants/${encodeURIComponent(tenantId)}/support-cases`,
      })) as { readonly supportCases: readonly SupportCaseRecord[] };
      return answer.supportCases;
    },

    async supportCase(input): Promise<SupportCaseRecord> {
      const answer = (await transport.request({
        method: "GET",
        path: `/v1/tenants/${encodeURIComponent(input.tenantId)}/support-cases/${encodeURIComponent(input.caseId)}`,
      })) as { readonly supportCase: SupportCaseRecord };
      return answer.supportCase;
    },

    async supportTimeline(input): Promise<readonly TimelineEntryRecord[]> {
      const answer = (await transport.request({
        method: "GET",
        path: `/v1/tenants/${encodeURIComponent(input.tenantId)}/support-cases/${encodeURIComponent(input.caseId)}/timeline`,
      })) as { readonly items: readonly TimelineEntryRecord[] };
      return answer.items;
    },

    async ownTrips(window): Promise<readonly OwnTrip[]> {
      const answer = (await transport.request({
        method: "GET",
        path: `/v1/me/trips${historyQuery(window)}`,
      })) as { readonly trips?: readonly OwnTrip[] };
      return answer.trips ?? [];
    },

    async ownCommerceOrders(window): Promise<readonly OwnCommerceOrder[]> {
      const answer = (await transport.request({
        method: "GET",
        path: `/v1/me/commerce-orders${historyQuery(window)}`,
      })) as { readonly orders?: readonly OwnCommerceOrder[] };
      return answer.orders ?? [];
    },

    async ownDeliveryOrders(window): Promise<readonly OwnDeliveryOrder[]> {
      const answer = (await transport.request({
        method: "GET",
        path: `/v1/me/delivery-orders${historyQuery(window)}`,
      })) as { readonly orders?: readonly OwnDeliveryOrder[] };
      return answer.orders ?? [];
    },

    refund(refundId): Promise<Refund> {
      return transport.request({
        method: "GET",
        path: `/v1/refunds/${encodeURIComponent(refundId)}`,
      }) as Promise<Refund>;
    },

    async consents(input): Promise<readonly ConsentEntry[]> {
      const answer = (await transport.request({
        method: "GET",
        path: `/v1/tenants/${encodeURIComponent(input.tenantId)}/customers/${encodeURIComponent(input.customerId)}/consents`,
      })) as { readonly history?: readonly ConsentEntry[] };
      return answer.history ?? [];
    },

    async withdrawConsent(input): Promise<ConsentEntry> {
      const answer = (await transport.request({
        method: "POST",
        path: `/v1/tenants/${encodeURIComponent(input.tenantId)}/customers/${encodeURIComponent(input.customerId)}/consents/${encodeURIComponent(input.grantId)}/withdrawals`,
        body: {
          evidenceReference: input.evidenceReference,
          // Both required by CUS-04 and both free strings with no published vocabulary. See
          // `CONSENT_SOURCE` below for where this build's one value is named and why it is there.
          source: CONSENT_SOURCE,
          jurisdiction: input.jurisdiction,
        },
        // No key: CUS-04 does not mark this idempotent. A withdrawal of an already-withdrawn grant
        // is refused with a 409, which is the service de-duplicating on its own record rather than
        // on a header this build invented.
      })) as { readonly consent: ConsentEntry };
      return answer.consent;
    },

    async grantConsent(input): Promise<ConsentEntry> {
      const answer = (await transport.request({
        method: "POST",
        path: `/v1/tenants/${encodeURIComponent(input.tenantId)}/customers/${encodeURIComponent(input.customerId)}/consents`,
        body: {
          purpose: input.purpose,
          purposeVersion: input.purposeVersion,
          policyVersion: input.policyVersion,
          evidenceReference: input.evidenceReference,
          source: CONSENT_SOURCE,
          jurisdiction: input.jurisdiction,
        },
      })) as { readonly consent: ConsentEntry };
      return answer.consent;
    },

    async preferences(customerId): Promise<readonly CustomerPreference[]> {
      const answer = (await transport.request({
        method: "GET",
        path: `/v1/customers/${encodeURIComponent(customerId)}/preferences`,
      })) as { readonly preferences?: readonly CustomerPreference[] };
      return answer.preferences ?? [];
    },

    async sessions(subjectId): Promise<readonly IdentitySession[]> {
      const answer = (await transport.request({
        method: "GET",
        path: "/v1/sessions",
        /*
         * Required, not optional, and the reason the catalogue's `answersACollection` flag was
         * corrected: this operation looks like a listing and answers nothing without a subject.
         * `CLAUDE.md` section 4 records that correction; a board built on the old reading would have
         * drawn only the viewer's own rows, and here the viewer's own rows are exactly what is
         * wanted.
         */
        query: { subjectId },
      })) as { readonly sessions?: readonly IdentitySession[] };
      return answer.sessions ?? [];
    },

    async revokeSession(input): Promise<IdentitySession> {
      const answer = (await transport.request({
        method: "POST",
        path: `/v1/sessions/${encodeURIComponent(input.sessionId)}/revocations`,
        body: { reason: input.reason },
        // No key: IAM-04 does not mark this idempotent, and revoking an already-revoked session is
        // answered by the service from its own record rather than from a header.
      })) as { readonly session: IdentitySession };
      return answer.session;
    },

    paymentOutcome(input): Promise<PaymentOutcomeView> {
      // A read, on every render and on every press of `Check again now`. `CLAUDE.md` section 2's
      // sentence about checking status is the whole of why that button is a GET and not a retry of
      // the command — and the design's own note says the same: "It is safe to tap any number of
      // times."
      return transport.request({
        method: "GET",
        path: `/v1/tenants/${encodeURIComponent(input.tenantId)}/customer-app/customers/${encodeURIComponent(input.customerId)}/payment-outcomes/${encodeURIComponent(input.executionId)}`,
      }) as Promise<PaymentOutcomeView>;
    },

    async raiseSafetyIncident(input): Promise<void> {
      // No key: SAF-01 does not mark this idempotent, and `messageId` is the field it de-duplicates
      // on. Sending a header the contract does not name would be a header the service ignores.
      await transport.request({
        method: "POST",
        path: `/v1/tenants/${encodeURIComponent(input.tenantId)}/trips/${encodeURIComponent(input.tripId)}/safety-incidents`,
        body: { messageId: input.messageId, command: input.command },
      });
    },

    async stopPlan(input): Promise<StopPlanView> {
      const answer = (await transport.request({
        method: "GET",
        path: `/v1/tenants/${encodeURIComponent(input.tenantId)}/stop-plans/${encodeURIComponent(input.planId)}`,
      })) as { readonly plan: StopPlanView };
      return answer.plan;
    },

    async address(addressId): Promise<AddressView> {
      const answer = (await transport.request({
        method: "GET",
        path: `/v1/addresses/${encodeURIComponent(addressId)}`,
      })) as { readonly address: AddressView };
      return answer.address;
    },

    /*
     * The wallet answers `WalletSummary` at the root rather than under a key.
     *
     * FIN-01's internal read answers the wallet at the root too (`return service.required(...)`),
     * and the public one keeps that shape. Three of the four reads below unwrap a key and this one
     * does not, which is the sort of inconsistency a generated client hides and a hand-written seam
     * has to state — so it is stated rather than smoothed over with a wrapper nobody sends.
     */
    wallet(walletId): Promise<WalletSummary> {
      return transport.request({
        method: "GET",
        path: `/v1/wallets/${encodeURIComponent(walletId)}`,
      }) as Promise<WalletSummary>;
    },

    accountBalances(input): Promise<AccountBalances> {
      return transport.request({
        method: "GET",
        path: `/v1/tenants/${encodeURIComponent(input.tenantId)}/accounts/${encodeURIComponent(input.accountId)}/balances`,
      }) as Promise<AccountBalances>;
    },

    async journalTransactions(input): Promise<readonly JournalTransaction[]> {
      const answer = (await transport.request({
        method: "GET",
        path: "/v1/journal-transactions",
        query: { currency: input.currency, limit: String(input.limit) },
      })) as { readonly transactions?: readonly JournalTransaction[] };
      /*
       * `?? []` rather than trusting the field, the same call `rideProducts` makes above.
       *
       * An answer with no `transactions` key is not an empty ledger — it is an answer this
       * application could not read — and the two are drawn differently. The wallet screens carry the
       * distinction in their own empty state rather than here.
       */
      return answer.transactions ?? [];
    },

    async paymentInstruments(tenantId): Promise<readonly PaymentInstrument[]> {
      const answer = (await transport.request({
        method: "GET",
        path: `/v1/tenants/${encodeURIComponent(tenantId)}/mobile-money/instruments`,
      })) as { readonly instruments?: readonly PaymentInstrument[] };
      return answer.instruments ?? [];
    },
  };
}
